// index.js
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Auth0Provider } from '@auth0/auth0-react';
import history from "./components/config/history";
import {
  isChunkLoadError,
  isChunkScriptErrorEvent,
  reloadPageForStaleChunks,
  restorePathAfterChunkReload,
} from './Utils/chunkLoadError';

// Handle ChunkLoadError - occurs when browser tries to load chunks that no longer exist
// This happens when main.js is cached but chunks have been updated in a new deployment
(function handleChunkLoadErrors() {
  if (window.__chunkErrorHandlerInitialized) {
    return;
  }
  window.__chunkErrorHandlerInitialized = true;

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }

    console.warn('[ChunkLoadError] Detected chunk load failure. Reloading page to fetch latest chunks...', event.reason);
    event.preventDefault();
    reloadPageForStaleChunks();
  });

  window.addEventListener('error', (event) => {
    const isChunkFailure = isChunkLoadError(event.error) || isChunkScriptErrorEvent(event);
    if (!isChunkFailure) {
      return;
    }

    console.warn('[ChunkLoadError] Detected chunk load failure. Reloading page to fetch latest chunks...', event.error || event.message);
    event.preventDefault();
    reloadPageForStaleChunks();
  });

  restorePathAfterChunkReload();
})();

// Helper function to load configuration
const loadConfig = (env) => {
  switch (env) {
    case 'development':
      return import('./auth_config.dev.json');
    case 'uat':
      return import('./auth_config.uat.json');
    case 'prod':
      return import('./auth_config.prod.json');
    default:
      return import('./auth_config.json');
  }
};

const Root = () => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const env = process.env.REACT_APP_ENV;
    loadConfig(env).then((configFile) => {
      setConfig(configFile.default);  // .default for dynamically imported JSON
    });
  }, []);

  if (!config) {
    return <div>Loading configuration...</div>;  // Loading state while the config is fetched
  }

  const onRedirectCallback = (appState) => {
    history.push(appState?.returnTo || window.location.pathname);
  };

  const providerConfig = {
    domain: config.domain,
    clientId: config.clientId,
    onRedirectCallback,
    authorizationParams: {
      redirect_uri: window.location.origin,
      ...(config.audience ? { audience: config.audience } : null),
    },
  };
  return (
    <Auth0Provider {...providerConfig}>
      <App />
    </Auth0Provider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
